/**
 * Squiggle Zustand Store
 * 
 * Global state management for the Squiggle mascot system.
 * Handles visibility, position, trail, flow state, and completion tracking.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import {
  SquiggleState,
  SquiggleStore,
  MoodType,
  Position,
  TrailPoint,
  TrailMode,
  BubbleContent,
  CompletionRecord,
} from '../components/Squiggle/types';

// Default home position (bottom-right corner, moved up to avoid overlap with content)
const DEFAULT_HOME_POSITION: Position = { x: 0.85, y: 0.78 };

// Initial state
const initialState: SquiggleState = {
  // Visibility
  enabled: true,
  visible: true, // Squiggle shown by default when enabled - acts as persistent mascot
  loading: false,
  
  // Character state
  mood: 'idle',
  talking: false,
  
  // Position (viewport-relative 0-1)
  position: { ...DEFAULT_HOME_POSITION },
  homePosition: { ...DEFAULT_HOME_POSITION },
  
  // Trail
  trailActive: false,
  trailMode: 'off',
  trailPoints: [],
  
  // Flow
  activeFlowId: null,
  currentStepIndex: 0,
  flowQueue: [],
  flowPaused: false,
  
  // Bubble
  bubbleVisible: false,
  bubbleContent: null,
  
  // Completion tracking
  completedFlows: {},
  milestones: {},
  
  // Session tracking (not persisted)
  sessionFlows: new Set(),
};

// Create the store with persistence for completion tracking
export const useSquiggleStore = create<SquiggleStore>()(
  persist(
    (set, get) => ({
      ...initialState,
      
      // ============================================
      // Visibility Actions
      // ============================================
      
      setEnabled: (enabled: boolean) => {
        set({ enabled });
        if (!enabled) {
          // Hide immediately when disabled
          set({
            visible: false,
            bubbleVisible: false,
            trailMode: 'clear',
            activeFlowId: null,
            flowQueue: [],
          });
        }
      },
      
      show: () => set({ visible: true }),
      
      hide: () => set({ 
        visible: false, 
        bubbleVisible: false,
        talking: false,
      }),
      
      // ============================================
      // Character Actions
      // ============================================
      
      setMood: (mood: MoodType) => set({ mood }),
      
      setTalking: (talking: boolean) => set({ talking }),
      
      // ============================================
      // Position Actions
      // ============================================
      
      setPosition: (position: Position) => set({ position }),
      
      setHomePosition: (homePosition: Position) => set({ homePosition }),
      
      returnHome: () => set((state) => ({ position: { ...state.homePosition } })),
      
      // ============================================
      // Trail Actions
      // ============================================
      
      setTrailMode: (trailMode: TrailMode) => {
        set({ trailMode, trailActive: trailMode === 'on' });
        if (trailMode === 'clear') {
          set({ trailPoints: [] });
        }
      },
      
      addTrailPoint: (point: TrailPoint) => {
        set((state) => ({
          trailPoints: [...state.trailPoints.slice(-500), point], // Keep last 500 points
        }));
      },
      
      clearTrailPoints: () => set({ trailPoints: [] }),
      
      // ============================================
      // Flow Actions
      // ============================================
      
      setActiveFlow: (activeFlowId: string | null) => {
        set({ activeFlowId, currentStepIndex: 0, flowPaused: false });
        if (activeFlowId) {
          // Add to session tracking
          const sessionFlows = new Set(get().sessionFlows);
          sessionFlows.add(activeFlowId);
          set({ sessionFlows });
        }
      },
      
      setCurrentStepIndex: (currentStepIndex: number) => set({ currentStepIndex }),
      
      queueFlow: (flowId: string) => {
        set((state) => ({
          flowQueue: [...state.flowQueue, flowId],
        }));
      },
      
      dequeueFlow: () => {
        const state = get();
        if (state.flowQueue.length === 0) return undefined;
        
        const [next, ...rest] = state.flowQueue;
        set({ flowQueue: rest });
        return next;
      },
      
      pauseFlow: () => set({ flowPaused: true }),
      
      resumeFlow: () => set({ flowPaused: false }),
      
      // ============================================
      // Bubble Actions
      // ============================================
      
      showBubble: (content: BubbleContent) => set({ 
        bubbleVisible: true, 
        bubbleContent: content,
        talking: true,
      }),
      
      hideBubble: () => set({ 
        bubbleVisible: false, 
        bubbleContent: null,
        talking: false,
      }),
      
      // ============================================
      // Completion Actions
      // ============================================
      
      markFlowCompleted: (flowId: string) => {
        set((state) => {
          const existing = state.completedFlows[flowId];
          const record: CompletionRecord = {
            flowId,
            completedAt: Date.now(),
            runCount: existing ? existing.runCount + 1 : 1,
          };
          
          return {
            completedFlows: {
              ...state.completedFlows,
              [flowId]: record,
            },
          };
        });
      },
      
      setMilestone: (key: string, value: boolean) => {
        set((state) => ({
          milestones: {
            ...state.milestones,
            [key]: value,
          },
        }));
      },
      
      hasCompletedFlow: (flowId: string) => {
        return flowId in get().completedFlows;
      },
      
      hasRunThisSession: (flowId: string) => {
        return get().sessionFlows.has(flowId);
      },
      
      // ============================================
      // Reset
      // ============================================
      
      reset: () => set({
        ...initialState,
        // Preserve enabled preference
        enabled: get().enabled,
        // Preserve completion tracking
        completedFlows: get().completedFlows,
        milestones: get().milestones,
      }),
    }),
    {
      name: 'myminu-squiggle',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        // Only persist these fields
        enabled: state.enabled,
        visible: state.visible,
        completedFlows: state.completedFlows,
        milestones: state.milestones,
      }),
    }
  )
);

// Selector hooks for performance
export const useSquiggleEnabled = () => useSquiggleStore((state) => state.enabled);
export const useSquiggleVisible = () => useSquiggleStore((state) => state.visible);
export const useSquiggleMood = () => useSquiggleStore((state) => state.mood);
export const useSquigglePosition = () => useSquiggleStore((state) => state.position);
export const useSquiggleBubble = () => useSquiggleStore(
  useShallow((state) => ({
    visible: state.bubbleVisible,
    content: state.bubbleContent,
  }))
);
export const useSquiggleFlowState = () => useSquiggleStore(
  useShallow((state) => ({
    activeFlowId: state.activeFlowId,
    currentStepIndex: state.currentStepIndex,
    paused: state.flowPaused,
  }))
);

export default useSquiggleStore;
